<template>
  <div class="device" :style="[deviceStyle, showInfo ? { zIndex: 9998 } : {}]">

    <div
    class="device-image-wrapper"
    :style="imgSize"
    :class="{ 'device-image-wrapper--active': showInfo }"
    :draggable="isDraggable"
    :id="`device-${deviceId}`"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @pointerdown.stop="wasDragging = false"
    @click.stop="!wasDragging && device.device_type !== 'alarm' && $emit('toggle-info', showInfo ? null : deviceId)"
    @dblclick.stop="device.device_type === 'camera' && onDblClick()"
    @mouseenter="onIconHoverEnter"
    @mouseleave="onIconHoverLeave"
    style="cursor: pointer;"
    >

      <!-- Cono SVG -->
      <svg v-if="showCone && isCamera" class="cone-svg" :style="coneSvgStyle">
        <path
          :d="conePath"
          fill="rgba(0,200,80,0.25)"
          stroke="rgba(0,200,80,0.8)"
          stroke-width="1.5"
        />
      </svg>

      <!-- Pulso de alarma — WebSocket para devices normales, isAlarmActive para alarmas genéricas -->
      <div
        v-if="device.device_type === 'alarm' ? isAlarmActive : showAlarm"
        class="circles"
      >
        <div class="circle1" :style="{ backgroundColor: device.device_type === 'alarm' ? (device.color || '#cf1111') : alarmStyle.pulseColor }"></div>
        <div class="circle2" :style="{ backgroundColor: device.device_type === 'alarm' ? (device.color || '#cf1111') : alarmStyle.pulseColor }"></div>
        <div class="circle3" :style="{ backgroundColor: device.device_type === 'alarm' ? (device.color || '#cf1111') : alarmStyle.pulseColor }"></div>
      </div>


      <!-- Icono del device -->
      <!-- Alarma con CustomIcon -->
      <CustomIcon
        v-if="device.device_type === 'alarm'"
        :name="device.icon || 'NotificationsActive'"
        :size="position.width || 30"
        :color="isAlarmActive ? '#ff0000' : (device.color_icon || device.color || '#9e9e9e')"
        :style="rotationStyle"
        :class="['device-img', { 'alarm-icon--active': isAlarmActive }]"
      />

      <!-- Icono del device — CustomIcon si tiene tag/rule icon -->
      <CustomIcon
        v-else-if="alarmStyle.icon"
        :name="alarmStyle.icon"
        :size="position.width || 30"
        :color="alarmStyle.iconColor"
        :style="rotationStyle"
        :class="['device-img', { 'alarm-icon--active': showAlarm }]"
      />

      <!-- DMP Panel Status -->
      <div
        v-else-if="device.device_type === 'dmp_panel_status'"
        class="dmp-status-card"
      >
        <div class="dmp-status-card__icon">
          <feather-icon
            v-if="device.icon"
            :icon="dmpStatusIcon"
            size="14"
            :style="{ color: device.value === 'Falla' ? '#cf1111' : '#6c757d' }"
          />
        </div>
        <div class="dmp-status-card__info">
          <span class="dmp-status-card__name">{{ device.name }}</span>
          <span
            class="dmp-status-card__value"
            :style="{ color: device.value === 'Falla' ? '#cf1111' : '#6c757d' }"
          >
            {{ device.value }}
          </span>
        </div>
      </div>

      <!-- DMP Panel Action card -->
      <div
        v-else-if="device.device_type === 'dmp_panel_action'"
        class="dmp-action-card"
      >
        <div class="dmp-action-card__icon">
          <feather-icon
            v-if="device.icon"
            :icon="dmpActionIcon"
            size="16"
            :style="{ color: device.color_icon || '#6c757d' }"
          />
        </div>
        <span class="dmp-action-card__name">{{ device.name }}</span>
        <!-- temporal debug -->
      </div>

      <!-- Imagen normal -->
      <img
        v-else
        :src="setImage(device)"
        class="device-img"
        :style="rotationStyle"
        draggable="false"
      />

      <!-- Popover Bootstrap-Vue (cambiar los status a alarmed) -->
      <b-popover
        :target="`device-${deviceId}`"
        :show="showInfo"
        placement="auto"
        triggers="manual"
        container="body"
        boundary="window"
        @hide="$emit('toggle-info', null)"
      >
        <template #title>{{ name }}</template>

      <div style="max-height: 450px; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain;">

         <!-- Skeleton -->
         <div v-if="isLoading" class="popover-skeleton">
            <div class="text-center my-2">
              <b-spinner small />
            </div>
            <div class="popover-skeleton__line popover-skeleton__line--title" />
            <div class="popover-skeleton__line" />
            <div class="popover-skeleton__line popover-skeleton__line--short" />
            <div class="popover-skeleton__line" />
         </div>

        <template v-else>
          <!-- Camera -->
          <template v-if="device.device_type === 'camera' && deviceData">
            <p class="mb-1">
              <strong>Estado:</strong>
              <b-badge
                :style="statusBadgeStyle"
                class="ml-1 device-status-badge"
              >
                {{ displayStatus.status }}
              </b-badge>
            </p>

            <p class="mb-1"><strong>Tipo:</strong> {{ device.camera_type }}</p>
            <p v-if="deviceData.camera" class="mb-1">
              <strong>RTSP:</strong> {{ deviceData.rtsp_available ? 'Sí' : 'No' }}
            </p>
            <p v-if="deviceData.camera" class="mb-0">
              <strong>Grabación previa:</strong> {{ deviceData.camera.delayed_record_minutes }}
            </p>
          </template>

          <!-- Alarm bridge panel -->
          <template v-else-if="device.device_type === 'alarm_bridge'">
            <p class="mb-1">
              <strong>Estado:</strong>
              <b-badge
                :style="statusBadgeStyle"
                class="ml-1 device-status-badge"
              >
                {{ displayStatus.status }}
              </b-badge>
            </p>

            <div v-if="panelLoading" class="text-center py-1">
              <b-spinner small />
            </div>

            <div v-else-if="panelList.length">
              <hr class="my-1" />
              <div
                v-for="(item, index) in panelList"
                :key="`${item.text}-${item.id}`"
                class="panel-item"
                style="margin-bottom: 5px;"
              >
                <div class="panel-item__header" @click.stop="togglePanel(index)">
                  <div class="panel-item__header-left">
                    <span class="panel-item__dot" :class="`panel-item__dot--${getStatusVariant(item.status)}`" />
                    <span class="panel-item__name">
                      {{ item.text }}
                      <span v-if="item.device_type === 'area'" class="panel-item__tag">area</span>
                    </span>
                  </div>
                  <span class="panel-item__chevron">{{ expandedPanel === index ? '▲' : '▼' }}</span>
                </div>

                <div v-show="expandedPanel === index" class="panel-item__body">
                  <span
                    class="panel-item__status-badge"
                    :class="`panel-item__status-badge--${getStatusVariant(item.status)}`"
                  >
                    {{ item.status }}
                  </span>

                  <div
                    v-for="(action, ai) in item.actions"
                    :key="ai"
                    class="device-popover__action d-flex justify-content-between align-items-center"
                    @click.stop="dispatchAction(item, action)"
                  >
                    <span>{{ action.data.action_name }}</span>
                    <feather-icon icon="PlayIcon" size="12" color="green" />
                  </div>

                  <!-- Sub-devices -->
                  <div v-if="item.devices && item.devices.length" class="mt-1">
                    <small class="text-muted d-block mb-1">Zonas</small>
                    <div
                      v-for="sub in item.devices"
                      :key="sub.id"
                      class="panel-item mb-1"
                    >
                      <div class="panel-item__header panel-item__header--sub" @click.stop="toggleSubPanel(sub.id)">
                        <div class="panel-item__header-left">
                          <span class="panel-item__dot" :class="`panel-item__dot--${getStatusVariant(sub.status)}`" />
                          <span class="panel-item__name">{{ sub.text }}</span>
                        </div>
                        <span class="panel-item__chevron">{{ expandedSubPanel === sub.id ? '▲' : '▼' }}</span>
                      </div>

                      <div v-show="expandedSubPanel === sub.id" class="panel-item__body">
                        <span
                          class="panel-item__status-badge"
                          :class="`panel-item__status-badge--${getStatusVariant(sub.status)}`"
                        >
                          {{ sub.status }}
                        </span>

                        <div
                          v-for="(action, si) in sub.actions"
                          :key="si"
                          class="device-popover__action d-flex justify-content-between align-items-center"
                          @click.stop="dispatchAction(sub, action)"
                        >
                          <span>{{ action.data.action_name }}</span>
                          <feather-icon icon="PlayIcon" size="12" color="green" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Sub-device -->
          <template v-else-if="device.device_type === 'alarm_bridge_device'">
            <p class="mb-1">
            <strong>Estado:</strong>
            <b-badge
              :style="statusBadgeStyle"
              class="ml-1 device-status-badge"
            >
              {{ displayStatus.status }}
            </b-badge>
          </p>

            <p class="mb-1"><strong>Tipo:</strong> {{ device.panel_device_type }}</p>

            <div v-if="panelLoading" class="text-center py-1">
              <b-spinner small />
            </div>

            <div v-else-if="panelDevice">
              <hr class="my-1" />
              <p class="mb-1"><strong>Acciones:</strong></p>
              <div
                v-for="(action, i) in panelDevice.actions"
                :key="i"
                class="device-popover__action d-flex justify-content-between align-items-center"
                @click.stop="dispatchAction(panelDevice, action)"
              >
                <span>{{ action.data.action_name }}</span>
                <feather-icon icon="PlayIcon" size="12" color="green" />
              </div>
            </div>
          </template>

          <!-- ── Density / Actuator ─────────────────────────── -->
          <template v-else-if="device.device_type === 'actuator' || device.camera_type === 'density_device'">
            <p class="mb-1">
            <strong>Estado:</strong>
            <b-badge
              :style="statusBadgeStyle"
              class="ml-1 device-status-badge"
            >
              {{ displayStatus.status }}
            </b-badge>
          </p>

            <p class="mb-1"><strong>Tipo:</strong> {{ device.camera_type }}</p>

            <div v-if="deviceData">
              <hr class="my-1" />
              <div
                class="device-popover__action d-flex justify-content-between align-items-center"
                @click.stop="runActuatorAction('info-actuator')"
              >
                <span>Información</span>
                <feather-icon icon="InfoIcon" size="12" color="green" />
              </div>
              <div
                class="device-popover__action d-flex justify-content-between align-items-center"
                @click.stop="runActuatorAction('density-test-shoot')"
              >
                <span>Disparo de prueba</span>
                <feather-icon icon="PlayIcon" size="12" color="green" />
              </div>
              <div
                class="device-popover__action d-flex justify-content-between align-items-center"
                @click.stop="runActuatorAction('density-shoot')"
              >
                <span>Disparo real</span>
                <feather-icon icon="PlayIcon" size="12" color="orange" />
              </div>
            </div>
          </template>

          <!-- ── Interphone ────────────────────────────────── -->
          <template v-else-if="device.device_type === 'interphone' || device.camera_type === 'interphone'">
            <p class="mb-1">
              <strong>Estado:</strong>
              <b-badge
                :style="statusBadgeStyle"
                class="ml-1 device-status-badge"
              >
                {{ displayStatus.status }}
              </b-badge>
            </p>

            <hr class="my-1" />
            <div
              class="device-popover__action d-flex justify-content-between align-items-center"
              @click.stop="runInterphoneAction('interphone-call')"
            >
              <span>Hacer una llamada</span>
              <feather-icon icon="PhoneIcon" size="12" color="green" />
            </div>
            <div
              class="device-popover__action d-flex justify-content-between align-items-center"
              @click.stop="runInterphoneAction('interphone-info')"
            >
              <span>Información</span>
              <feather-icon icon="InfoIcon" size="12" color="blue" />
            </div>
          </template>

          <!-- ── Router ────────────────────────────────────── -->
          <template v-else-if="device.camera_type === 'router'">
            <hr class="my-1" />
            <div
              class="device-popover__action d-flex justify-content-between align-items-center"
              @click.stop="runRouterAction('activate-digital-output')"
            >
              <span>Activar salida digital</span>
              <feather-icon icon="PlayIcon" size="12" color="green" />
            </div>
            <div
              class="device-popover__action d-flex justify-content-between align-items-center"
              @click.stop="runRouterAction('disable-digital-output')"
            >
              <span>Desactivar salida digital</span>
              <feather-icon icon="PauseIcon" size="12" color="red" />
            </div>
          </template>

          <!-- Fallback -->
          <template v-else>
            <p class="mb-1"><strong>Tipo:</strong> {{ device.device_type || '—' }}</p>
            <p class="mb-1"><strong>Estado:</strong> {{ displayStatus.status || '—' }}</p>
            <p class="mb-0"><strong>ID:</strong> {{ device.id_camera_proxy }}</p>
          </template>
        </template>

      </div>
      </b-popover>

      <!-- Tooltip via Teleport — siempre encima de todo -->
      <Teleport to="body">
        <div
          v-if="showHover && tooltipPos.x"
          class="device-hover-tooltip"
          :style="{
            position: 'fixed',
            left: tooltipPos.x + 'px',
            top:  tooltipPos.y + 'px',
            transform: 'translateX(-50%)',
          }"
        >
          <!-- Nombre y zona -->
          <!-- Solo si no hay currentPanelItem -->
          <div v-if="!currentPanelItem" class="device-hover-tooltip__row">
            <span class="device-hover-tooltip__label">Dispositivo</span>
            <span class="device-hover-tooltip__value">
              {{ name }}
              <span v-if="sourceIdLabel" style="opacity:0.6; margin-left:4px;">#{{ sourceIdLabel }}</span>
            </span>
          </div>

          <!-- Tipo — desde panelDevice o panel del panelList si ya cargó, sino del device -->
          <div class="device-hover-tooltip__row">
            <span class="device-hover-tooltip__label">Tipo</span>
            <span class="device-hover-tooltip__value">
              {{
                panelDevice ? panelDevice.device_type :
                (panelList.length ? panelList[0].device_type : null) ||
                device.camera_type || device.device_type || '—'
              }}
            </span>
          </div>

          <!-- Si es alarm_bridge y ya se cargó el panel -->
          <!-- <template v-if="device.device_type === 'alarm_bridge' && panelList.length">
            <div class="device-hover-tooltip__divider" />
            <div
              v-for="item in panelList.slice(0, 4)"
              :key="item.id"
              class="device-hover-tooltip__row"
            >
              <span class="device-hover-tooltip__label">{{ item.text }}</span>
              <span
                class="device-hover-tooltip__badge"
                :class="{
                  'device-hover-tooltip__badge--normal':  ['NORMAL','DISARMED','OFF','UNLOCKED'].includes((item.status || '').toUpperCase()),
                  'device-hover-tooltip__badge--warning': ['OPEN','BYPASSED','ARMED'].includes((item.status || '').toUpperCase()),
                  'device-hover-tooltip__badge--alarm':   ['ALARM','BURGLARY','ALARMED','FAULT'].includes((item.status || '').toUpperCase()),
                  'device-hover-tooltip__badge--offline': ['OFFLINE'].includes((item.status || '').toUpperCase()),
                }"
              >
                {{ item.status || '—' }}
              </span>
            </div>
          </template> -->

          <!-- Si es alarm_bridge_device y ya se cargó el sub-device -->
          <!-- <template v-if="device.device_type === 'alarm_bridge_device' && panelDevice">
            <div class="device-hover-tooltip__divider" />
            <div class="device-hover-tooltip__row">
              <span class="device-hover-tooltip__label">Estado panel</span>
              <span
                class="device-hover-tooltip__badge"
                :class="{
                  'device-hover-tooltip__badge--normal':  ['NORMAL','DISARMED'].includes((panelDevice.status || '').toUpperCase()),
                  'device-hover-tooltip__badge--alarm':   ['ALARM','BURGLARY','ALARMED'].includes((panelDevice.status || '').toUpperCase()),
                  'device-hover-tooltip__badge--warning': ['ARMED','OPEN'].includes((panelDevice.status || '').toUpperCase()),
                }"
              >
                {{ panelDevice.status || '—' }}
              </span>
            </div>
          </template> -->

          <!-- Si es cámara y ya se cargó -->
          <!-- <template v-if="device.device_type === 'camera' && deviceData">
            <div class="device-hover-tooltip__divider" />
            <div class="device-hover-tooltip__row">
              <span class="device-hover-tooltip__label">RTSP</span>
              <span class="device-hover-tooltip__value">{{ deviceData.rtsp_available ? 'Sí' : 'No' }}</span>
            </div>
          </template> -->

          <!-- Alarma genérica -->
          <!-- <template v-if="device.device_type === 'alarm'">
            <div class="device-hover-tooltip__divider" />
            <div class="device-hover-tooltip__row">
              <span class="device-hover-tooltip__label">Alarma</span>
              <span
                class="device-hover-tooltip__badge"
                :class="isAlarmActive ? 'device-hover-tooltip__badge--alarm' : 'device-hover-tooltip__badge--normal'"
              >
                {{ isAlarmActive ? 'Activa' : 'Normal' }}
              </span>
            </div>
          </template> -->

          <!-- Última activación desde latest_alarm del panelList -->
          <template v-if="currentItemLatestAlarm && Object.keys(currentItemLatestAlarm).length">
            <!-- <div class="device-hover-tooltip__divider" /> -->

            <div class="device-hover-tooltip__row">
              <span class="device-hover-tooltip__label">Dispositivo</span>
              <span class="device-hover-tooltip__value">
                {{ currentPanelItem ? `${currentPanelItem.text} (${currentPanelItem.id})` : currentItemLatestAlarm.device_string }}
              </span>
            </div>

            <!-- Área padre — solo para zones unicamente-->
            <div v-if="parentArea" class="device-hover-tooltip__row">
              <span class="device-hover-tooltip__label">Área</span>
              <span class="device-hover-tooltip__value">
                {{ parentArea.text }}
                <span style="opacity:0.6; margin-left:4px;">({{ parentArea.id }})</span>
              </span>
            </div>

            <div class="device-hover-tooltip__row">
              <span class="device-hover-tooltip__label">Estado</span>
              <span
                class="device-hover-tooltip__badge"
                :style="{
                  backgroundColor: currentItemLatestAlarm.color || getDefaultColor(currentItemLatestAlarm.status),
                  color: 'white'
                }"
              >
                {{ currentItemLatestAlarm.status }}
              </span>
            </div>

            <div class="device-hover-tooltip__row">
              <span class="device-hover-tooltip__label">Última alarma</span>
              <span class="device-hover-tooltip__value">{{ currentItemLatestAlarm.last_activity_ct_formatted }}</span>
            </div>

          </template>

          <!-- Fallback lastAlarmTime si no hay latest_alarm -->
          <div v-else-if="lastAlarmTime" class="device-hover-tooltip__row">
            <span class="device-hover-tooltip__label">Última activación</span>
            <span class="device-hover-tooltip__value">{{ lastAlarmTime }}</span>
          </div>

          <div v-if="isLoading || panelLoading" class="device-hover-tooltip__row" style="justify-content: center;">
            <b-spinner small style="width: 12px; height: 12px; color: rgba(255,255,255,0.5);" />
          </div>

        </div>
      </Teleport>

    </div>

    <!-- Label -->
    <div
      v-if="showLabel && device.device_type !== 'dmp_panel_status' && device.device_type !== 'dmp_panel_action'"
      class="device-label"
      :style="device.device_type === 'alarm' ? {
        backgroundColor: device.color || 'rgba(0,0,0,0.55)',
      } : {}"
      @mouseenter="onHoverEnter"
      @mouseleave="onHoverLeave"
      @mousemove="onHoverMove"
    >
      <span
        v-if="device.device_type !== 'alarm'"
        class="status-dot"
        :class="{ off: device.status !== 'Online' }"
      ></span>
      <span
        class="device-label__name"
        :style="device.device_type === 'alarm' ? { color: device.text_color || 'white' } : {}"
      >
        {{ name }}
      </span>
    </div>

    <!-- Badge de identificador de origen -->
    <div
      v-if="sourceIdLabel"
      class="zone-badge"
      :class="{ 'zone-badge--alarm': showAlarm }"
      :style="showAlarm ? { backgroundColor: alarmColor } : {}"
    >
      <span style="padding-right:0px;"></span>{{ sourceIdLabel }}
    </div>

    <div v-if="isDraggable" class="device-drag-hint" />

    <!-- Botones de alarma -->
    <div
      v-if="device.device_type === 'alarm' && showAlarmActions"
      class="alarm-actions"
    >
      <!-- Activar -->
      <button
        class="alarm-btn"
        :class="isAlarmActive ? 'alarm-btn--dispatch-active' : 'alarm-btn--dispatch'"
        v-b-tooltip.hover.bottom="'Activar Alarma'"
        @click.stop="runAlarmAction('dispatch')"
      >
        <feather-icon icon="BellIcon" size="10" />
      </button>

      <!-- Restablecer -->
      <button
        class="alarm-btn alarm-btn--cancel"
        v-b-tooltip.hover.bottom="'Restablecer Alarma'"
        @click.stop="runAlarmAction('cancel')"
      >
        <feather-icon icon="BellOffIcon" size="10" />
      </button>
    </div>

    <!-- Botones de acción DMP -->
    <div
      v-if="device.device_type === 'dmp_panel_action' && device.actions && Object.keys(device.actions).length"
      class="dmp-action-buttons"
    >
      <button
        v-for="(url, actionKey) in device.actions"
        :key="actionKey"
        class="dmp-action-btn"
        v-b-tooltip.hover.bottom="actionKey"
        @click.stop="runDmpAction(actionKey, url)"
      >
        <feather-icon icon="PlayIcon" size="10" />
      </button>
    </div>

    <!-- Floating cam -->
    <Teleport to="body">
      <FloatingCamV2
        v-if="showFloatingCam"
        :camera-id="device.camera_id"
        :camera-proxy-id="device.id_camera_proxy"
        :uuid="`floating-${device.id_camera_proxy}`"
        @destroy="showFloatingCam = false"
      />
    </Teleport>

  </div>
</template>

<script>
import { ref, watch, nextTick, computed, onMounted } from "@vue/composition-api"
import Teleport from 'vue2-teleport'
import layoutMixin from "@/mixins/layoutMixin"
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig"
import setDeviceImage from "./useList.js"
import CustomIcon from '@/components/CustomIcon.vue'
import CamSlot from '../CamSlot.vue'
import FloatingCamV2 from "@/components/FloatingCamV2.vue";
import store from "@/store"
import axios from "@axios"
import Swal from "sweetalert2"

const DEFAULT_SIZE = 30

// Fuera del export default
const _deviceInfoCache = {}
const _refreshTrigger  = ref({})
const cacheKeyForDevice = (device) => {
  const context = device.floor_plant_id ? `floor_${device.floor_plant_id}_` : ''
  const type = device.device_type === 'alarm_bridge_device' ? 'panel' : 'device'
  const proxyId = device.device_type === 'alarm_bridge_device'
    ? device.panel_proxy_id
    : device.id_camera_proxy

  return `${context}${type}_${proxyId}`
}

const invalidateCacheByProxy = (proxyId) => {
  Object.keys(_deviceInfoCache).forEach((key) => {
    if (key.endsWith(`panel_${proxyId}`) || key.endsWith(`device_${proxyId}`)) {
      delete _deviceInfoCache[key]
    }
  })
}

export const triggerPanelRefresh = (proxyId) => {
  // Borrar cache para forzar recarga del backend
  delete _deviceInfoCache[`panel_${proxyId}`]
  delete _deviceInfoCache[`device_${proxyId}`]
  _refreshTrigger.value = { ..._refreshTrigger.value, [proxyId]: Date.now() }
}

// actualiza cache con datos del evento WebSocket
export const updateDeviceInCache = (event) => {
  const deviceData = event.data?.data
  const proxyId    = event.data?.id_camera_proxy
  if (!deviceData || !proxyId) return

  Object.keys(_deviceInfoCache).forEach(key => {
    const cached = _deviceInfoCache[key]
    if (!cached?.raw) return

    const raw = cached.raw
    for (const section in raw) {
      if (section === 'panel') continue
      if (!Array.isArray(raw[section])) continue
      for (const item of raw[section]) {
        if (String(item.device_proxy_id) === String(proxyId)) {
          item.status       = deviceData.status
          item.latest_alarm = deviceData
        }
        if (item.devices && Array.isArray(item.devices)) {
          for (const sub of item.devices) {
            if (String(sub.device_proxy_id) === String(proxyId)) {
              sub.status       = deviceData.status
              sub.latest_alarm = deviceData
            }
          }
        }
      }
    }
  })
  _refreshTrigger.value = { ..._refreshTrigger.value, [proxyId]: Date.now() }
}

// Exportar función para invalidar desde fuera
export const invalidateDeviceCache = (proxyId) => {
  invalidateCacheByProxy(proxyId)
}

export const invalidateAllCache = () => {
  Object.keys(_deviceInfoCache).forEach(k => delete _deviceInfoCache[k])
}

export default {
  name: "Device",
  components: { CustomIcon, CamSlot, FloatingCamV2, Teleport },
  mixins: [layoutMixin],
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e) }
        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) { document.removeEventListener('click', el._clickOutside) }
    }
  },

  props: {
    position:           { type: Object,  required: true },
    name:               { type: String,  default: ""    },
    device:             { type: Object,  required: true },
    isCamera:           { type: Boolean, default: false },
    showLabel:          { type: Boolean, default: true  },
    activeInfoDeviceId: { default: null                 },
    scale:              { type: Number,  default: 1     },
    showAlarm:  { type: Boolean, default: false },
    alarmColor: { type: String,  default: '#cf1111' },
    showAlarmActions: { type: Boolean, default: true },
  },

  data() {
    return {
      showCone:     this.position?.cone?.visible  ?? false,
      coneDistance: this.position?.cone?.distance ?? 80,
      coneAngle:    this.position?.cone?.angle    ?? 45,
      wasDragging:  false,
      cameraModalKey: 0,
      SLOT_TYPES,
      showFloatingCam: false,
    }
  },

  setup(props , { emit }) {
    const isLoading      = ref(false)
    const deviceData     = ref(null)
    const panelDevice    = ref(null)
    const panelList      = ref([])
    const panelLoading   = ref(false)
    const popoverPosition = ref('right')
    const expandedPanel    = ref(null)
    const expandedSubPanel = ref(null)
    const isAlarmActive = ref(props.device.active_alarm ?? false)

    const showHover    = ref(false)
    const lastAlarmTime = ref(null)
    const tooltipPos   = ref({ x: 0, y: 0 })
    let hoverTimer = null
    const parentArea = ref(null)


    const loadDeviceInfo = async () => {
      const cacheKey = cacheKeyForDevice(props.device)

      // Si ya está cacheado, restaurar y salir
      if (_deviceInfoCache[cacheKey]) {
        const cached = _deviceInfoCache[cacheKey]
        deviceData.value  = cached.deviceData || null

        // Restaurar panelList con splice para reactividad Vue 2
        if (cached.panelList && cached.panelList.length) {
          panelList.value.splice(0, panelList.value.length, ...cached.panelList)
        } else {
          panelList.value.splice(0, panelList.value.length)
        }

        if (props.device.device_type === 'alarm_bridge_device' && cached.raw) {
          panelDevice.value = findDeviceInPanel(cached.raw, props.device.name, props.device.id_camera_proxy)
        } else {
          panelDevice.value = cached.panelDevice || null
        }

        if (props.device.panel_device_type === 'zone' && cached.raw?.areas) {
          for (const area of cached.raw.areas) {
            if (area.devices && Array.isArray(area.devices)) {
              const found = area.devices.find(sub =>
                String(sub.device_proxy_id) === String(props.device.id_camera_proxy)
              )
              if (found) {
                parentArea.value = area
                break
              }
            }
          }
        }

        return
      }

      isLoading.value    = true
      deviceData.value   = null
      panelDevice.value  = null
      panelList.value.splice(0, panelList.value.length)

      try {
        if (props.device.device_type === 'camera') {
          const { data } = await axios.get(
            `${window.__env.VUE_APP_BASE_URL}/v1/proxy_camera/${props.device.id_camera_proxy}`
          )
          deviceData.value = data.data
          _deviceInfoCache[cacheKey] = { deviceData: deviceData.value, panelList: [], panelDevice: null, raw: null }

        } else if (props.device.device_type === 'alarm_bridge') {
          isLoading.value    = false
          panelLoading.value = true
          await store.dispatch("alarm_panels/getInfo", {
            device_proxy_id: props.device.id_camera_proxy,
            floor_plant_id: props.device.floor_plant_id,
            floor_device_proxy_id: props.device.id_camera_proxy,
            withDevices:     true,
          })
          const raw      = store.getters["alarm_panels/getItems"].devices || {}
          const flattened = flattenPanel(raw)

          // splice para reactividad Vue 2
          panelList.value.splice(0, panelList.value.length, ...flattened)
          _deviceInfoCache[cacheKey] = { deviceData: null, panelList: flattened, panelDevice: null, raw }

        } else if (props.device.device_type === 'alarm_bridge_device') {
          const proxyId = props.device.panel_proxy_id || null
          if (!proxyId) { isLoading.value = false; return }
          isLoading.value    = false
          panelLoading.value = true
          await store.dispatch("alarm_panels/getInfo", {
            device_proxy_id: proxyId,
            floor_plant_id: props.device.floor_plant_id,
            floor_device_proxy_id: props.device.id_camera_proxy,
            withDevices:     true,
          })
          const raw = store.getters["alarm_panels/getItems"].devices || {}
          panelDevice.value = findDeviceInPanel(raw, props.device.name, props.device.id_camera_proxy)

          // Buscar área padre solo si es zone
          if (props.device.panel_device_type === 'zone' && raw.areas) {
            for (const area of raw.areas) {
              if (area.devices && Array.isArray(area.devices)) {
                const found = area.devices.find(sub =>
                  String(sub.device_proxy_id) === String(props.device.id_camera_proxy)
                )
                if (found) {
                  parentArea.value = area
                  break
                }
              }
            }
          }
          _deviceInfoCache[cacheKey] = { deviceData: null, panelList: [], panelDevice: null, raw }

        } else if (props.device.device_type === 'actuator' || props.device.camera_type === 'density_device') {
          const { data } = await axios.get(
            `${window.__env.VUE_APP_BASE_URL}/v1/services/actuators/info/${props.device.id_camera_proxy}`
          )
          deviceData.value = data.data
          _deviceInfoCache[cacheKey] = { deviceData: deviceData.value, panelList: [], panelDevice: null, raw: null }

        } else if (props.device.device_type === 'interphone' || props.device.camera_type === 'interphone') {
          isLoading.value = false
          return
        }

      } catch (err) {
        console.error('Error cargando info:', err)
      } finally {
        isLoading.value    = false
        panelLoading.value = false
      }
    }

    const flattenPanel = (raw) => {
      const result = []
      for (const key in raw) {
        if (key === 'panel') { result.push(raw[key]); continue }
        if (Array.isArray(raw[key])) {
          raw[key].forEach(d => {
            result.push(d)
            // También incluir sub-devices (zones dentro de areas)
            if (d.devices && Array.isArray(d.devices)) {
              d.devices.forEach(sub => result.push(sub))
            }
          })
        }
      }
      return result
    }

    const findDeviceInPanel = (raw, name, proxyId) => {
      for (const key in raw) {
        if (key === 'panel') continue
        if (Array.isArray(raw[key])) {
          // Buscar en primer nivel por nombre O por proxy_id
          const found = raw[key].find(d =>
            d.text === name ||
            (proxyId && String(d.device_proxy_id) === String(proxyId))
          )
          if (found) return found

          // Buscar en sub-devices
          for (const d of raw[key]) {
            if (d.devices && Array.isArray(d.devices)) {
              const sub = d.devices.find(sd =>
                sd.text === name ||
                (proxyId && String(sd.device_proxy_id) === String(proxyId))
              )
              if (sub) return sub
            }
          }
        }
      }
      return null
    }

    const getStatusVariant = (status) => {
      if (!status) return 'secondary'
      const s = status.toUpperCase()
      if (['NORMAL', 'ARMED', 'ONLINE', 'UNLOCKED', 'OFF'].includes(s)) return 'success'
      if (['OPEN', 'BYPASSED', 'TROUBLE'].includes(s))                   return 'warning'
      if (['ALARM', 'OFFLINE', 'FAULT'].includes(s))                     return 'danger'
      return 'secondary'
    }

    const togglePanel    = (i)  => { expandedPanel.value    = expandedPanel.value    === i  ? null : i  }
    const toggleSubPanel = (id) => { expandedSubPanel.value = expandedSubPanel.value === id ? null : id }

    const dispatchAction = async (device, action) => {
      const result = await Swal.fire({
        title: `${action.data.action_name} ${device.text}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await axios.get(action.data.url)
        await Swal.fire({ title: 'Comando ejecutado', icon: 'success' })
        await loadDeviceInfo()
      } catch (err) {
        await Swal.fire({ title: 'Error al ejecutar el comando', icon: 'error' })
      }
    }

    const checkPopoverPosition = () => {
      const wrapper = document.querySelector('.device-image-wrapper--active')
      if (!wrapper) return

      const rect       = wrapper.getBoundingClientRect()
      const spaceRight = window.innerWidth - rect.right
      const spaceLeft  = rect.left

      popoverPosition.value = spaceRight >= spaceLeft ? 'right' : 'left'
    }

    const runInterphoneAction = (action) => {
      const { EventBus } = require('@/libs/event-bus')
      if (action === 'interphone-call') {
        EventBus.emit('call_device', { id: props.device.id_camera_proxy })
      } else if (action === 'interphone-info') {
        emit('interphone-action', { action, device: props.device })
      }
    }

    const runRouterAction = async (action) => {
      const titles = {
        'activate-digital-output':  '¿Activar la salida digital?',
        'disable-digital-output':   '¿Desactivar la salida digital?',
      }
      const commands = {
        'activate-digital-output':  'activate-router-do',
        'disable-digital-output':   'deactivate-router-do',
      }
      const result = await Swal.fire({
        title: titles[action],
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await axios.get(`${window.__env.VUE_APP_BASE_URL}/v1/command/run-command-by-name`, {
          params: { name: commands[action], id: props.device.id_camera_proxy }
        })
        await Swal.fire({ title: 'Comando ejecutado', icon: 'success' })
      } catch {
        await Swal.fire({ title: 'Error al ejecutar el comando', icon: 'error' })
      }
    }

    const runActuatorAction = async (action) => {
      if (action === 'info-actuator') {
        emit('actuator-info', { device: props.device })
        return
      }

      const titles = {
        'density-test-shoot': '¿Ejecutar disparo de prueba?',
        'density-shoot':      '¿Ejecutar disparo real?',
      }
      const commands = {
        'density-test-shoot': `run-density-test-shoot`,
        'density-shoot':      `run-density-shoot`,
      }
      const result = await Swal.fire({
        title: titles[action],
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await axios.get(`${window.__env.VUE_APP_BASE_URL}/v1/command/run-command-by-name`, {
          params: { name: commands[action], id: props.device.id_camera_proxy }
        })
        await Swal.fire({ title: 'Comando ejecutado', icon: 'success' })
      } catch {
        await Swal.fire({ title: 'Error al ejecutar', icon: 'error' })
      }
    }

    const runAlarmAction = async (type) => {
      const url = type === 'dispatch'
        ? props.device.dispatch_url
        : props.device.cancel_url

      if (!url) {
        await Swal.fire({
          title: type === 'dispatch'
            ? 'No hay acción de activación disponible'
            : 'No hay acción de restauración disponible',
          icon: 'info',
          confirmButtonText: 'Aceptar',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
        return
      }

      const result = await Swal.fire({
        title: type === 'dispatch' ? '¿Disparar alarma?' : '¿Restaurar alarma?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false,
      })

      if (!result.isConfirmed) return

      try {
        await axios.get(`${window.__env.VUE_APP_BASE_URL}${url}`)
        await Swal.fire({ title: 'Ejecutado correctamente', icon: 'success' })
        isAlarmActive.value = type === 'dispatch'
        emit('alarm-action', { device: props.device, type })
      } catch {
        await Swal.fire({ title: 'Error al ejecutar', icon: 'error' })
      }
    }

    const runDmpAction = async (actionKey, url) => {
      if (!url) {
        await Swal.fire({
          title: 'No hay URL disponible para esta acción',
          icon: 'info',
          confirmButtonText: 'Aceptar',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
        return
      }

      const result = await Swal.fire({
        title: `¿Ejecutar "${actionKey}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false,
      })

      if (!result.isConfirmed) return

      try {
        await axios.get(`${window.__env.VUE_APP_BASE_URL}${url}`)
        await Swal.fire({ title: 'Ejecutado correctamente', icon: 'success' })
      } catch {
        await Swal.fire({ title: 'Error al ejecutar', icon: 'error' })
      }
    }

    const onHoverEnter = (e) => {
      hoverTimer = setTimeout(async () => {
        showHover.value = true
        updateTooltipPos(e)

        // Cargar info si aún no se ha cargado
        const alreadyLoaded = panelList.value.length > 0 || panelDevice.value || deviceData.value
        if (!alreadyLoaded && props.device.device_type !== 'alarm') {
          await loadDeviceInfo()
        }
      }, 300)
    }

    const onHoverMove = (e) => {
      if (showHover.value) updateTooltipPos(e)
    }

    const onHoverLeave = () => {
      if (hoverTimer) {
        clearTimeout(hoverTimer)
        hoverTimer = null
      }
      if (iconHoverTimer) {
        clearTimeout(iconHoverTimer)
        iconHoverTimer = null
      }
      showHover.value = false
    }

    const updateTooltipPos = (e) => {
      tooltipPos.value = {
        x: e.clientX,
        y: e.clientY - 12,
      }
    }

    let iconHoverTimer = null

    const onIconHoverEnter = (e) => {
      iconHoverTimer = setTimeout(async () => {
        showHover.value = true
        updateTooltipPos(e)
        const alreadyLoaded = panelList.value.length > 0 || panelDevice.value || deviceData.value
        if (!alreadyLoaded && props.device.device_type !== 'alarm') {
          await loadDeviceInfo()
        }
      }, 300)
    }

    const onIconHoverLeave = () => {
      if (iconHoverTimer) {
        clearTimeout(iconHoverTimer)
        iconHoverTimer = null
      }
      if (!showHover.value) return
      showHover.value = false
    }

    const currentItemLatestAlarm = computed(() => {
      // Primero del prop device (inyectado por el padre)
      if (props.device.latest_alarm && 
          !Array.isArray(props.device.latest_alarm) &&
          Object.keys(props.device.latest_alarm).length > 0) {
        return props.device.latest_alarm
      }
      // Fallback: panelDevice cargado por hover/click
      if (panelDevice.value) {
        if (panelDevice.value.latest_alarm &&
            !Array.isArray(panelDevice.value.latest_alarm) &&
            Object.keys(panelDevice.value.latest_alarm).length > 0) {
          return panelDevice.value.latest_alarm
        }
        if (panelDevice.value.devices && Array.isArray(panelDevice.value.devices)) {
          const sub = panelDevice.value.devices.find(d =>
            String(d.device_proxy_id) === String(props.device.id_camera_proxy)
          )
          if (sub && sub.latest_alarm && !Array.isArray(sub.latest_alarm)) {
            return sub.latest_alarm
          }
        }
      }
      return null
    })

    // Watch currentItemLatestAlarm para actualizar icono/color en el plano
    watch(currentItemLatestAlarm, (alarm) => {
      if (!alarm) return

      const isAlarmed = ['ALARMED', 'BURGLARY', 'ALARM', 'FAULT'].includes(
        (alarm.status || '').toUpperCase()
      )

      const style = resolveAlarmStyle(alarm, isAlarmed)

      emit('update-alarm-style', {
        device:     props.device,
        alarmColor: style.pulseColor,
        alarmIcon:  style.icon,
        showAlarm:  isAlarmed,
      })
    })

    const currentPanelItem = computed(() => {
      if (panelDevice.value) return panelDevice.value

      if (panelList.value && panelList.value.length) {
        const myProxyId = props.device.id_camera_proxy
        return panelList.value.find(item =>
          String(item.device_proxy_id) === String(myProxyId) ||
          item.text === props.device.name
        ) || null
      }

      return null
    })

    const dmpActionIcon = computed(() => {
      if (!props.device.icon) return 'ZapIcon'
      const raw = props.device.icon.replace('feather icon-', '')
      return raw
        .split('-')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join('') + 'Icon'
    })

    const displayStatus = computed(() => {
      const latestColor  = currentItemLatestAlarm.value?.color || null
      const latestStatus = currentItemLatestAlarm.value?.status || null

      console.log('panelList:', panelList.value)
      console.log('panel encontrado:', panelList.value.find(i => i.device_type === 'panel'))

      // alarm_bridge_device — si ya cargó panelDevice
      if (props.device.device_type === 'alarm_bridge_device') {
        if (panelDevice.value) {
          const status = panelDevice.value.status
          const s = status.toUpperCase()
          const useLatest = ['ALARMED','BURGLARY','FAULT'].includes(s)
          const color = (useLatest && latestColor) ? latestColor : getDefaultColor(status)
          return { status, color }
        }
      }

      // alarm_bridge — si ya cargó panelList
      if (props.device.device_type === 'alarm_bridge') {
        const panel = panelList.value.find(i => i.device_type === 'panel')
        if (panel) {
          const status = panel.status
          const s = status.toUpperCase()
          // Solo usar latestColor si el panel está en alarma
          const useLatest = ['ALARMED','BURGLARY','FAULT'].includes(s)
          const color = (useLatest && latestColor) ? latestColor : getDefaultColor(status)
          return { status, color }
        }
      }

      // camera, actuator, etc — usar status de conectividad
      return {
        status: props.device.status || '—',
        color:  getDefaultColor(props.device.status)
      }
    })

    const getDefaultColor = (status) => {
      const s = (status || '').toUpperCase()
      if (['ALARMED', 'BURGLARY', 'ALARM', 'FAULT', 'BYPASSED'].includes(s)) return '#cf1111'
      if (['ARMED'].includes(s))                                  return '#ff9900'
      if (['DISARMED', 'NORMAL', 'RESTORED'].includes(s))        return '#28a745'
      if (['BYPASSED', 'OPEN'].includes(s))                      return '#f0ad4e'
      if (['ONLINE'].includes(s))                                 return '#28a745'
      if (['OFFLINE'].includes(s))                                return '#6c757d'
      return '#6c757d'
    }

    const statusBadgeStyle = computed(() => ({
      '--device-status-color': displayStatus.value.color || getDefaultColor(displayStatus.value.status),
    }))

    const getStatusStyle = (item) => {
      if (!item) return {}
      return item.color ? { backgroundColor: item.color, color: 'white' } : {}
    }

    const getStatusClass = (item) => {
      if (!item || item.color) return ''
      const s = (item.status || '').toUpperCase()
      if (['ALARMED','BURGLARY','ARMED','FAULT'].includes(s)) return 'badge-danger'
      if (['RESTORED','NORMAL','DISARMED'].includes(s))       return 'badge-success'
      if (['BYPASSED','OPEN'].includes(s))                    return 'badge-warning'
      return 'badge-secondary'
    }

    watch(
      () => props.activeInfoDeviceId,
      async (val) => {
        const myId = props.device.id_camera_proxy ?? props.device.id_generic ?? props.device.id
        if (val === myId) {
          await nextTick()
          checkPopoverPosition()
          await loadDeviceInfo()
        }
      }
    )

    //Para los casos que nos dijo bibi
    const resolveAlarmStyle = (data, isAlarmed = false) => {
      if (!data) return { iconColor: '#9e9e9e', pulseColor: '#cf1111', icon: null }

      const tag  = data.tag  && data.tag.icon  ? data.tag  : null
      const rule = data.rule && data.rule.icon ? data.rule : null

      const tagColor  = tag  ? (tag.icon_color || null) : null
      const ruleColor = rule ? (rule.color     || null) : null
      const tagIcon   = tag  ? tag.icon  : null
      const ruleIcon  = rule ? rule.icon : null

      if (tag) {
        if (isAlarmed) {
          return {
            // Caso 1 — sin rule: icono tag, color rojo
            // Caso 2 — con rule: icono rule, color rule
            icon:       ruleIcon  || tagIcon,     
            iconColor:  ruleColor || '#cf1111',
            pulseColor: '#cf1111',
          }
        } else {
          // Normal/restaurado — siempre vuelve al tag
          return {
            icon:       tagIcon,
            iconColor:  tagColor || '#9e9e9e',
            pulseColor: '#cf1111',
          }
        }
      } else {
        if (isAlarmed) {
          // Caso 3 — sin rule: icono default, color default
          // Caso 4 — con rule: icono rule, color rule
          return {
            icon:       ruleIcon  || null,
            iconColor:  ruleColor || '#9e9e9e',
            pulseColor: '#cf1111',
          }
        } else {
          return {
            icon:       null,
            iconColor:  '#9e9e9e',
            pulseColor: '#cf1111',
          }
        }
      }
    }

    const alarmStyle = computed(() => {
      const alarm = currentItemLatestAlarm.value
      if (!alarm) return { iconColor: '#9e9e9e', pulseColor: '#cf1111', icon: null }

      // isAlarmed viene del showAlarm del WebSocket O del status del latest_alarm
      const isAlarmedByWS     = props.showAlarm
      const isAlarmedByStatus = ['ALARMED', 'BURGLARY', 'ALARM', 'FAULT'].includes(
        (alarm.status || '').toUpperCase()
      )
      const isAlarmed = isAlarmedByWS || isAlarmedByStatus

      return resolveAlarmStyle(alarm, isAlarmed)
    })

    const dmpStatusIcon = computed(() => {
      if (!props.device.icon) return 'ActivityIcon'
      const raw = props.device.icon.replace('feather icon-', '')
      return raw
        .split('-')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join('') + 'Icon'
    })

    const hasDmpActions = computed(() => {
      if (props.device.device_type !== 'dmp_panel_action') return false
      if (!props.device.actions) return false
      return typeof props.device.actions === 'object' &&
            !Array.isArray(props.device.actions) &&
            Object.keys(props.device.actions).length > 0
    })

    const latestAlarmColor = computed(() => {
      if (!currentItemLatestAlarm.value) return '#6c757d'
      const tag  = currentItemLatestAlarm.value.tag  || null
      const rule = currentItemLatestAlarm.value.rule  || null
      return tag?.color || rule?.color || currentItemLatestAlarm.value.color || '#6c757d'
    })

    watch(
      () => props.device.active_alarm,
      (val) => {
        isAlarmActive.value = val
      }
    )

    // Watch profundo sobre device para detectar cambios del padre via WebSocket
    watch(
      () => props.device.latest_alarm,
      (newAlarm) => {
        if (!newAlarm || Array.isArray(newAlarm)) return

      },
      { deep: true }
    )

    // Watch sobre showAlarm y alarmColor del prop para el zone-badge
    watch(
      () => [props.showAlarm, props.alarmColor],
      ([newShowAlarm, newAlarmColor]) => {
      }
    )

    return {
      isLoading, deviceData,
      panelDevice, panelList, panelLoading,
      popoverPosition,
      expandedPanel, expandedSubPanel,
      getStatusVariant,
      togglePanel, toggleSubPanel,
      dispatchAction,
      runInterphoneAction,
      runRouterAction,
      runActuatorAction,
      runAlarmAction,
      isAlarmActive,

      showHover,
      lastAlarmTime,
      tooltipPos,
      onHoverEnter,
      onHoverMove,
      onHoverLeave,
      currentItemLatestAlarm,
      currentPanelItem,
      onIconHoverEnter,
      onIconHoverLeave,
      getStatusStyle,
      getStatusClass,
      displayStatus,
      statusBadgeStyle,
      getDefaultColor,
      parentArea,
      runDmpAction,
      dmpActionIcon,

      resolveAlarmStyle,
      dmpStatusIcon,
      hasDmpActions,
      latestAlarmColor,
      alarmStyle,
    }
  },

  computed: {
    sourceIdLabel() {
      const sourceId = this.device?.source_id

      if (sourceId === null || sourceId === undefined) {
        return null
      }

      const label = String(sourceId).trim()

      return label || null
    },
    deviceId() {
    return this.device.id_camera_proxy ?? this.device.id_generic ?? this.device.id
    },
    showInfo() {
      return this.activeInfoDeviceId === this.deviceId
    },
    deviceStyle() {
      return { left: `${this.position.x}px`, top: `${this.position.y}px` }
    },
    imgSize() {
      const w = this.position.width  || DEFAULT_SIZE
      const h = this.position.height || DEFAULT_SIZE
      return { width: `${w}px`, height: `${h}px` }
    },
    rotationStyle() {
      return { transform: `rotate(${this.position.degree || 0}deg)` }
    },
    coneSvgSize() { return this.coneDistance + 20 },
    coneSvgStyle() {
      const w = this.position.width  || DEFAULT_SIZE
      const h = this.position.height || DEFAULT_SIZE
      const size = this.coneSvgSize
      return {
        position: 'absolute',
        left: `${w / 2 - size}px`, top: `${h / 2 - size}px`,
        width: `${size * 2}px`, height: `${size * 2}px`,
        overflow: 'visible', pointerEvents: 'none', zIndex: 5,
      }
    },
    coneOrigin() { const s = this.coneSvgSize; return { x: s, y: s } },
    coneBaseAngle() { return ((this.position.degree || 0) - 90) * (Math.PI / 180) },
    conePath() {
      const { x: ox, y: oy } = this.coneOrigin
      const r = this.coneDistance
      const half = (this.coneAngle / 2) * (Math.PI / 180)
      const base = this.coneBaseAngle
      const x1 = ox + r * Math.cos(base - half), y1 = oy + r * Math.sin(base - half)
      const x2 = ox + r * Math.cos(base + half), y2 = oy + r * Math.sin(base + half)
      return `M ${ox} ${oy} L ${x1} ${y1} A ${r} ${r} 0 ${this.coneAngle > 180 ? 1 : 0} 1 ${x2} ${y2} Z`
    },
    popoverScaleStyle() {
      const inv = 1 / (this.scale || 1)
      return {
        transform:       `scale(${inv})`,
        transformOrigin: 'top left',
      }
    },
    isDraggable() {
      const t = this.device.device_type
      const c = this.device.camera_type
      return (
        t === 'camera'        ||
        t === 'alarm_bridge'  ||
        c === 'router'        ||
        c === 'interphone'    ||
        c === 'density_device'||
        c === 'actuator'
      )
    },
  },

  methods: {
    setImage(device) {
      return setDeviceImage(device)
    },

    onDragEnd() {
      setTimeout(() => { this.wasDragging = false }, 100)
    },

    onDragStart(e) {
      this.wasDragging = true
      const { device } = this
      const type   = device.device_type
      const camera = device.camera_type
      const name   = device.name
      console.log('device en drag:', JSON.stringify(this.device, null, 2))
      if (type === 'camera') {
        this.configSlotData(SLOT_TYPES.CAM_LIVE, name, {
          id:              device.id_camera_proxy,
          id_camera_proxy:   device.id_camera_proxy,
          camera_proxy_id:   device.id_camera_proxy,
          camera_id:         device.camera_id,
          name:              device.name,
          proxy_type:        'camera',
          proxy_camera_type: device.camera_type,
          device_type:       device.device_type,
          camera_type:       device.camera_type,
          status:            device.status,
      }, SOURCES.CAMERA)
      } else if (type === 'alarm_bridge') {
        this.setSlotData(
          SLOT_TYPES.ALARM_BRIDGE_PANEL,
          name,
          { device: { ...device } },
          SOURCES.ALARM
          )
      } else if (camera === 'router') {
        this.setSlotData(SLOT_TYPES.INFO, name, { camera: device }, SOURCES.ALARM)

      } else if (camera === 'interphone') {
        this.setSlotData(SLOT_TYPES.INTERPHONE, device.id_camera_proxy, { data: device })

      } else if (camera === 'density_device' || camera === 'actuator') {
        const urlActuators = `${window.__env.VUE_APP_BASE_URL}/v1/services/actuators/info/`
        this.setSlotData(
          SLOT_TYPES.INFO,
          device.id_camera_proxy,
          { data: { url: urlActuators + device.id_camera_proxy } },
          SOURCES.ACTUATOR
        )
      }
    },

    configSlotData(slotType, name, data, source = null) {
      this.setSlotData(slotType, name, { camera: data }, source)
    },

    onDblClick() {
      if (this.device.device_type !== 'camera') return
      this.showFloatingCam = true
    },

  }
}
</script>

<style lang="scss">
.device {
  position: absolute;
  z-index: 10;
  cursor: default;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: visible;
}

.device-image-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.device-image-wrapper--active { z-index: 10000; }

.device-img {
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  position: relative;
  z-index: 2;
}
.cone-svg   { pointer-events: none; }

.device-label {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.55);
  border-radius: 4px;
  padding: 1px 6px;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  max-width: 120px;
}

.device-label__name { font-size: 10px; color: white; padding-left: 4px; overflow: hidden; text-overflow: ellipsis; }

.status-dot { width: 7px; height: 7px; min-width: 7px; border-radius: 50%; background: #28a745; display: inline-block; }
.status-dot.off { background: #dc3545; }

/* ── Popover ─────────────────────────────────────────── */
.device-popover {
  position: absolute;
  top: 0;
  width: 240px;
  max-height: 600px;
  overflow-y: auto;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 9999;
  font-size: 12px;
  white-space: normal;
  cursor: default;
}

.device-popover--right { left: calc(100% + 8px); right: auto; }
.device-popover--left  { right: calc(100% + 8px); left: auto; }

.device-popover__arrow {
  position: absolute;
  top: 8px; left: -6px;
  width: 10px; height: 10px;
  background: white;
  border-left: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  transform: rotate(45deg);
}

.device-popover__arrow--left {
  left: auto; right: -6px;
  border-left: none; border-bottom: none;
  border-right: 1px solid #dee2e6;
  border-top: 1px solid #dee2e6;
}

.device-popover__title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 4px;
}

.device-popover__action {
  padding: 3px 0;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}
.device-popover__action:last-child { border-bottom: none; }
.device-popover__action:hover { color: #385d7b; }

.device-status-badge {
  background-color: var(--device-status-color, #6c757d) !important;
  color: #ffffff !important;
}

/* ── Panel items ─────────────────────────────────────── */
.panel-item__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 8px;
  cursor: pointer;
  user-select: none;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}
.panel-item__header:hover { background: #e9ecef; }
.panel-item__header--sub  { background: #fff; font-size: 11px; }

.panel-item__header-left { display: flex; align-items: center; gap: 5px; }
.panel-item__chevron     { font-size: 9px; color: #6c757d; }

.panel-item__dot { width: 7px; height: 7px; min-width: 7px; border-radius: 50%; display: inline-block; }
.panel-item__dot--success  { background: #28a745; }
.panel-item__dot--warning  { background: #ff9900; }
.panel-item__dot--danger   { background: #dc3545; }
.panel-item__dot--secondary { background: #6c757d; }

.panel-item__name { font-size: 11px; font-weight: 500; }
.panel-item__tag  { font-size: 9px; color: #6c757d; background: #e9ecef; border-radius: 3px; padding: 1px 4px; margin-left: 3px; }

.panel-item__body {
  padding: 6px 8px;
  border: 1px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 4px 4px;
}

.panel-item__status-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  margin-bottom: 5px;
  color: white;
}
.panel-item__status-badge--success  { background: #28a745; }
.panel-item__status-badge--warning  { background: #ff9900; }
.panel-item__status-badge--danger   { background: #dc3545; }
.panel-item__status-badge--secondary { background: #6c757d; }

/* ── Skeleton ─────────────────────────────────────────── */
.popover-skeleton { padding: 4px 0; }
.popover-skeleton__line {
  height: 10px;
  border-radius: 4px;
  margin-bottom: 8px;
  background: linear-gradient(90deg, #e9ecef 0%, #f8f9fa 50%, #e9ecef 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
.popover-skeleton__line--title { height: 14px; width: 70%; margin-bottom: 12px; }
.popover-skeleton__line--short { width: 50%; }

@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}

.device-drag-hint {
  width: 100%;
  height: 2px;
  border-radius: 2px;
  background: #28a745;
  margin-top: 3px;
  opacity: 1;
  transition: opacity 0.2s ease;
  min-width: 20px;
}

.circles {
  > div {
    animation: growAndFade 3s infinite ease-out;
    animation-delay: 0.8s;
    animation-fill-mode: both;
    border-radius: 50%;
    height: 100%;
    position: absolute;
    width: 100%;
    left: 0;
    top: 0;
  }

  .circle1 { animation-delay: 0.8s; }
  .circle2 { animation-delay: 2.8s; }
  .circle3 { animation-delay: 3.8s; }
}

@keyframes growAndFade {
  0%   { opacity: .7; transform: scale(0); }
  100% { opacity: 0;  transform: scale(2.5); }
}

.alarm-icon--active {
  animation: alarmIconPulse 0.8s ease-in-out infinite alternate;
}

@keyframes alarmIconPulse {
  0%   { opacity: 1;   filter: drop-shadow(0 0 4px #cf1111); }
  100% { opacity: 0.5; filter: drop-shadow(0 0 10px #cf1111); }
}

.device-image-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: visible;
}

.zone-badge {
  min-width: 16px;
  height: 16px;
  padding: 0 6px;
  border-radius: 4px;
  background: #6c757d;
  color: white;
  font-size: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 3px;
  transition: background-color 0.3s ease;

  &--alarm {
    animation: badgePulse 1s ease-in-out infinite alternate;
  }
}

.alarm-actions {
  display: flex;
  gap: 3px;
  margin-top: 3px;
}

.alarm-btn {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border: none;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &--dispatch {
    background: #a71d2a;
    color: white;

    &:hover {
      background: #821621;
    }
  }

  &--dispatch-active {
    background: #dc3545;
    color: white;
    animation: alarmPulseBtn 1s ease-in-out infinite alternate;

    &:hover {
      background: #a71d2a;
    }
  }

  &--cancel {
    background: rgba(239, 68, 68, 0.42);
    color: #c53f3f;

    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  }
}

@keyframes alarmPulseBtn {
  0%   { opacity: 1;   transform: scale(1);    }
  100% { opacity: 0.6; transform: scale(0.95); }
}

/* SIN scoped — el elemento está en body via Teleport */
.device-hover-tooltip {
  z-index: 999999;
  background: rgba(20, 20, 20, 0.92);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 8px 10px;
  min-width: 180px;
  max-width: 320px;
  width: max-content;
  white-space: nowrap;
  pointer-events: none;
  backdrop-filter: blur(4px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.35);
  margin-top: 20px;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
    font-size: 11px;

    &:not(:last-child) {
      border-bottom: 1px solid rgba(255,255,255,0.07);
      padding-bottom: 4px;
      margin-bottom: 2px;
    }
  }

  &__label {
    color: rgba(255,255,255,0.5);
    font-size: 10px;
    flex-shrink: 0;
  }

  &__value {
    color: white;
    font-size: 11px;
    font-weight: 500;
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: none;
    white-space: nowrap;
  }

  &__divider {
    border-top: 1px solid rgba(255,255,255,0.1);
    margin: 4px 0;
  }

  &__hint {
    font-size: 9px;
    color: rgba(255,255,255,0.3);
    text-align: center;
    margin-top: 4px;
    font-style: italic;
  }

  &__badge {
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 10px;

    &--online  { background: #28a745; color: white; }
    &--offline { background: #6c757d; color: white; }
    &--alarm   { background: #dc3545; color: white; }
    &--normal  { background: #28a745; color: white; }
    &--warning { background: #ff9900; color: white; }
  }
}

.dmp-status-card {
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 6px 10px;
  gap: 8px;
  width: max-content;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  cursor: default;
  white-space: nowrap;

  &__icon {
    width: 28px;
    height: 28px;
    background: #f5f5f5;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__name {
    font-size: 11px;
    font-weight: 500;
    color: #333;
    line-height: 1.2;
  }

  &__value {
    font-size: 10px;
    font-weight: 400;
    line-height: 1.2;
  }
}

.dmp-action-card {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 5px;
  gap: 4px;
  width: 74px;
  height: 60px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  white-space: nowrap;

  &__icon {
    width: 32px;
    height: 32px;
    background: #f5f5f5;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__name {
    font-size: 9px;
    font-weight: 500;
    color: #333;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }
}

.dmp-action-buttons {
  position: absolute;
  bottom: -26px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 3px;
  justify-content: center;
  white-space: nowrap;
  z-index: 15;
}

.dmp-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -4px;
  gap: 3px;
  padding: 2px 6px;
  border: none;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  background: #007bff;
  color: white;

  &:hover { background: #0056b3; }
}

</style>
